/**
 * Client-only session state (Zustand).
 *
 * The authoritative session is server-side (resolved by TanStack Query in useSession); this store
 * holds only ephemeral, per-visit UI facts that must NOT be persisted or sent anywhere — e.g. the
 * "consent acknowledged this visit" flag, and the locally-held recording blob (WP-F5) that stays on
 * the device until the server confirms the upload. Deliberately not persisted to storage.
 */
import { create } from 'zustand';

import type { SignResult } from '@/lib/api-types';
import type { CompletedRecording } from '@/lib/recorder';

/**
 * The completed take, held between `stop()` and a confirmed upload.
 *
 * `token`-stamped so a take can never be offered back under a DIFFERENT link: this is a third
 * party's voice, and the one thing worse than losing it is handing it to the wrong session.
 */
export interface HeldRecording extends CompletedRecording {
  /** The `/r/:token` link this take was recorded under. */
  token: string;
  /** True when the take ended because something interrupted it (call, backgrounding, track end). */
  interrupted: boolean;
}

export interface SessionState {
  /**
   * The token every field below belongs to, or null before the first link is bound.
   *
   * **EVERY OTHER FIELD IN THIS STORE IS TOKEN-SCOPED.** Not one of them is a fact about the
   * device or the person — each is a fact about one reference request, learned from one link:
   *
   * | field                 | scope        | why |
   * |-----------------------|--------------|-----|
   * | `consentAcknowledged` | token-scoped | consent is captured per request, server-side, against that request's attestation. |
   * | `micChecked`          | token-scoped | closest to global (a mic is a device fact), but the flow's first hard rule is PRIME FIRST — a fresh referee on a fresh link must get the plain sentence and their own tap, never a check someone else passed. Currently write-only, so clearing it costs nothing and pre-empts a reader who would skip the prime. |
   * | `heldRecording`       | token-scoped | a third party's voice. Also stamped with its own token, as the read-side half of the same rule. |
   * | `signResult`          | token-scoped | THE reason this exists: it is the only place the SPA is ever told a verification code, and one referee must never be shown another's. |
   *
   * So the rule is the simple one — a different token starts from `initial`. If a genuinely
   * global fact ever lands here (a device capability, a dismissed banner), it must be held
   * OUTSIDE this store rather than exempted from the reset, or this table stops being true.
   */
  tokenScope: string | null;
  /**
   * Bind this state to `token`, clearing everything if it belongs to a different link.
   *
   * Three cases, and the two no-ops are the load-bearing ones:
   *  - same token → **no-op**. Every screen calls `useSession`, and each step is a fresh mount, so
   *    a clear-on-mount would wipe the flow's own state mid-flow (Confirm→Record would lose the
   *    consent ack and bounce; Sign→Done would lose the signature and show the revisit face).
   *  - no token bound yet → stamp it and clear NOTHING. A fresh store has nothing to clear, and
   *    this is what lets a caller seed state before the first render.
   *  - a different token → reset to `initial`, then stamp.
   */
  bindToken: (token: string) => void;

  /** Set true once the referee has acknowledged the in-flow consent notice this visit. */
  consentAcknowledged: boolean;
  setConsentAcknowledged: (value: boolean) => void;

  /** True once the mic check has passed this visit (drives the record step, WP-F5). */
  micChecked: boolean;
  setMicChecked: (value: boolean) => void;

  /**
   * The completed take awaiting upload, or null.
   *
   * **It lives here rather than in `Record`'s own state because ANY unmount of that route would
   * otherwise destroy it** — a reload, an iOS tab eviction, or (until WP-F5b) the consent redirect.
   * The recorder produces ONE blob at `stop()` and nothing is uploaded until "Use this recording",
   * so losing the blob loses the whole three minutes.
   *
   * **In memory only, and never persisted** (no IndexedDB, no storage): this is the most sensitive
   * data the product handles, the privacy policy describes no device-side store, and a blob that
   * outlives the tab is a blob nobody has promised to delete. The corollary is that it must be
   * dropped the moment it stops being needed — `clearRecording` on upload/"Start over"/a request
   * that has left the flow, and `bindToken` when another link is opened.
   */
  heldRecording: HeldRecording | null;
  /** Hold the take just produced for `token` (replaces any previous one). */
  holdRecording: (token: string, recording: CompletedRecording, interrupted: boolean) => void;
  /** Drop the held take — uploaded, discarded via "Start over", or otherwise finished with. */
  clearRecording: () => void;

  /**
   * The signature THIS VISIT produced — the verification code + timestamp the sign response
   * returned (WP-F7). It lives here, and only here, because the referee `/context` wire carries
   * neither field: the sign response is the one and only place the SPA is ever told the code.
   *
   * **Deliberately not persisted, and that is the honest behaviour rather than a limitation.** It
   * is what splits the Done screen's two faces: set → the referee just signed, so the ceremony and
   * their code are theirs to see; unset (a fresh tab, a refresh, a link re-opened next week) → the
   * calm already-signed page, which does NOT invent a code it was never given. Their code reaches
   * them by the signed-copy email, which is the durable copy; a code cached in storage would only
   * ever be a stale guess at what the server said once.
   */
  signResult: SignResult | null;
  setSignResult: (value: SignResult) => void;

  /**
   * Clear every client-only flag AND the token binding, i.e. back to a just-loaded tab. Used by
   * the tests to isolate a visit; the app itself clears through `bindToken`, which keeps the
   * binding so the next same-token mount is correctly recognised as a no-op.
   */
  reset: () => void;
}

const initial = {
  tokenScope: null,
  consentAcknowledged: false,
  micChecked: false,
  heldRecording: null,
  signResult: null,
};

export const useSessionStore = create<SessionState>((set) => ({
  ...initial,
  bindToken: (token) =>
    set((state) => {
      if (state.tokenScope === token) return state; // same link, a later screen — keep everything
      if (state.tokenScope === null) return { tokenScope: token }; // first bind: nothing to clear
      return { ...initial, tokenScope: token }; // a DIFFERENT link — none of this is theirs
    }),
  setConsentAcknowledged: (value) => set({ consentAcknowledged: value }),
  setMicChecked: (value) => set({ micChecked: value }),
  holdRecording: (token, recording, interrupted) =>
    set({
      heldRecording: {
        token,
        blob: recording.blob,
        durationSeconds: recording.durationSeconds,
        realtimeTranscript: recording.realtimeTranscript,
        interrupted,
      },
    }),
  clearRecording: () => set({ heldRecording: null }),
  setSignResult: (value) => set({ signResult: value }),
  reset: () => set({ ...initial }),
}));
